import type { ServiceResponse } from "@/api/axios";
import {
  PROGRESS_BASELINE_SOURCE,
  PROGRESS_BETTER_DIRECTION,
  PROGRESS_CHART_KIND,
  PROGRESS_DATA_STATE,
  PROGRESS_OUTCOME_COUNT_BASIS,
  PROGRESS_SCALE,
  PROGRESS_SEVERITY_BAND,
  PROGRESS_STATUS_CODE,
  PROGRESS_STATUS_TONE,
} from "@/domains/progress/models/constants";
import type { ProgressResponse } from "@/domains/progress/models/types";

/** The shared worked examples of the `GET /webapp/progress` contract. All data is synthetic. */
const PROFILING_COMPLETED_AT = "2026-03-02T14:05:00+00:00";
const CHECK_IN_1_AT = "2026-04-01T10:12:00+00:00";
const CHECK_IN_2_AT = "2026-05-02T09:40:00+00:00";
const CHECK_IN_3_AT = "2026-06-01T18:24:00+00:00";
const CHECK_IN_4_AT = "2026-07-01T08:05:00+00:00";

const GOAL_SUB_CAPTION = "Reported difficulty. 1 is no difficulty, 10 is unable to do it.";
const SYMPTOM_SUB_CAPTION = "Reported intensity from your check-ins, 1 to 5.";
const QUALITY_OF_LIFE_SUB_CAPTION = "Reported overall well-being, 1 to 5. Higher is better.";

const GOAL_SCALE = { ...PROGRESS_SCALE.GOAL, betterDirection: PROGRESS_BETTER_DIRECTION.LOWER };
const SYMPTOM_SCALE = {
  ...PROGRESS_SCALE.SYMPTOM_GROUP,
  betterDirection: PROGRESS_BETTER_DIRECTION.LOWER,
};
const QUALITY_OF_LIFE_SCALE = {
  ...PROGRESS_SCALE.QUALITY_OF_LIFE,
  betterDirection: PROGRESS_BETTER_DIRECTION.HIGHER,
};

export const PROGRESS_RETURNING_PATIENT_FIXTURE: ProgressResponse = {
  schemaVersion: 2,
  isNewPatient: false,
  generatedAt: "2026-09-22T12:00:00+00:00",
  intro: {
    text: "Here is how your goals and symptoms have moved since you started.",
  },
  goals: [
    {
      key: "goal_1",
      kind: PROGRESS_CHART_KIND.GOAL,
      title: "Walking my dog around the block",
      subCaption: GOAL_SUB_CAPTION,
      scale: GOAL_SCALE,
      status: {
        code: PROGRESS_STATUS_CODE.GETTING_EASIER,
        label: "Getting Easier",
        tone: PROGRESS_STATUS_TONE.POSITIVE,
      },
      dataState: PROGRESS_DATA_STATE.OK,
      baseline: {
        value: 8,
        recordedAt: PROFILING_COMPLETED_AT,
        source: PROGRESS_BASELINE_SOURCE.PROFILING,
      },
      latest: { checkInId: 5214, recordedAt: CHECK_IN_4_AT, value: 5 },
      points: [
        { checkInId: 4812, recordedAt: CHECK_IN_1_AT, value: 7 },
        { checkInId: 4930, recordedAt: CHECK_IN_2_AT, value: 7 },
        { checkInId: 5077, recordedAt: CHECK_IN_3_AT, value: 6 },
        { checkInId: 5214, recordedAt: CHECK_IN_4_AT, value: 5 },
      ],
      changeFromBaseline: -3,
      idealLine: {
        baseline: 8,
        severityBand: PROGRESS_SEVERITY_BAND.SEVERE,
        medianImprovement: 2.4,
        idealScore: 5.6,
        horizonAt: CHECK_IN_4_AT,
        patientCount: 63,
        severityMatched: true,
        caption:
          "Based on 63 patients who report the same kind of symptom as you and started at a similar level of difficulty.",
      },
      symptoms: [],
    },
    {
      key: "goal_2",
      kind: PROGRESS_CHART_KIND.GOAL,
      title: "Sleeping through the night without waking",
      subCaption: GOAL_SUB_CAPTION,
      scale: GOAL_SCALE,
      status: {
        code: PROGRESS_STATUS_CODE.HOLDING,
        label: "Holding",
        tone: PROGRESS_STATUS_TONE.NEUTRAL,
      },
      dataState: PROGRESS_DATA_STATE.OK,
      baseline: {
        value: 6,
        recordedAt: PROFILING_COMPLETED_AT,
        source: PROGRESS_BASELINE_SOURCE.PROFILING,
      },
      latest: { checkInId: 5214, recordedAt: CHECK_IN_4_AT, value: 6 },
      points: [
        { checkInId: 4812, recordedAt: CHECK_IN_1_AT, value: 6 },
        { checkInId: 4930, recordedAt: CHECK_IN_2_AT, value: null },
        { checkInId: 5077, recordedAt: CHECK_IN_3_AT, value: 6 },
        { checkInId: 5214, recordedAt: CHECK_IN_4_AT, value: 6 },
      ],
      changeFromBaseline: 0,
      idealLine: {
        baseline: 6,
        severityBand: PROGRESS_SEVERITY_BAND.MODERATE,
        medianImprovement: 1.8,
        idealScore: 4.2,
        horizonAt: CHECK_IN_4_AT,
        patientCount: 41,
        severityMatched: false,
        caption:
          "Based on 41 patients who report the same kind of symptom as you, across all starting levels.",
      },
      symptoms: [],
    },
  ],
  symptomGroups: [
    {
      key: "symptom_group_pain",
      kind: PROGRESS_CHART_KIND.SYMPTOM_GROUP,
      title: "Pain",
      subCaption: SYMPTOM_SUB_CAPTION,
      scale: SYMPTOM_SCALE,
      status: {
        code: PROGRESS_STATUS_CODE.IMPROVING,
        label: "Improving",
        tone: PROGRESS_STATUS_TONE.POSITIVE,
      },
      dataState: PROGRESS_DATA_STATE.OK,
      baseline: {
        value: 4,
        recordedAt: PROFILING_COMPLETED_AT,
        source: PROGRESS_BASELINE_SOURCE.PROFILING,
      },
      latest: { checkInId: 5214, recordedAt: CHECK_IN_4_AT, value: 2 },
      points: [
        { checkInId: 4812, recordedAt: CHECK_IN_1_AT, value: 4 },
        { checkInId: 4930, recordedAt: CHECK_IN_2_AT, value: 3 },
        { checkInId: 5077, recordedAt: CHECK_IN_3_AT, value: 3 },
        { checkInId: 5214, recordedAt: CHECK_IN_4_AT, value: 2 },
      ],
      changeFromBaseline: -2,
      idealLine: {
        baseline: 4,
        severityBand: PROGRESS_SEVERITY_BAND.SEVERE,
        medianImprovement: 1.2,
        idealScore: 2.8,
        horizonAt: CHECK_IN_4_AT,
        patientCount: 34,
        severityMatched: true,
        caption:
          "Based on 34 patients who report the same kind of symptom as you and started at a similar level of intensity.",
      },
      symptoms: [
        { code: "pain", label: "pain" },
        { code: "joint_or_arthritis_pain", label: "joint or arthritis pain" },
      ],
    },
    {
      key: "symptom_group_sleep",
      kind: PROGRESS_CHART_KIND.SYMPTOM_GROUP,
      title: "Sleep",
      subCaption: SYMPTOM_SUB_CAPTION,
      scale: SYMPTOM_SCALE,
      status: {
        code: PROGRESS_STATUS_CODE.JUST_STARTED,
        label: "Just Started",
        tone: PROGRESS_STATUS_TONE.NEUTRAL,
      },
      dataState: PROGRESS_DATA_STATE.NOT_SCORED,
      baseline: null,
      latest: null,
      points: [],
      changeFromBaseline: null,
      idealLine: null,
      symptoms: [
        { code: "difficulty_falling_staying_asleep", label: "difficulty falling/staying asleep" },
      ],
    },
  ],
  qualityOfLife: {
    key: "quality_of_life",
    kind: PROGRESS_CHART_KIND.QUALITY_OF_LIFE,
    title: "Overall quality of life",
    subCaption: QUALITY_OF_LIFE_SUB_CAPTION,
    scale: QUALITY_OF_LIFE_SCALE,
    status: {
      code: PROGRESS_STATUS_CODE.IMPROVING,
      label: "Improving",
      tone: PROGRESS_STATUS_TONE.POSITIVE,
    },
    dataState: PROGRESS_DATA_STATE.OK,
    baseline: {
      value: 2,
      recordedAt: PROFILING_COMPLETED_AT,
      source: PROGRESS_BASELINE_SOURCE.PROFILING,
    },
    latest: { checkInId: 5214, recordedAt: CHECK_IN_4_AT, value: 3 },
    points: [
      { checkInId: 4930, recordedAt: CHECK_IN_2_AT, value: 2 },
      { checkInId: 5077, recordedAt: CHECK_IN_3_AT, value: 3 },
      { checkInId: 5214, recordedAt: CHECK_IN_4_AT, value: 3 },
    ],
    changeFromBaseline: 1,
    idealLine: {
      baseline: 2,
      severityBand: PROGRESS_SEVERITY_BAND.SEVERE,
      medianImprovement: 1.5,
      idealScore: 3.5,
      horizonAt: CHECK_IN_4_AT,
      patientCount: 45,
      severityMatched: false,
      caption:
        "Based on 45 patients who report the same kind of symptom as you, across all starting levels.",
    },
    symptoms: [],
  },
  unscoredSymptoms: [{ code: "migraines_headaches", label: "migraines/headaches" }],
  outcomes: {
    applicable: true,
    items: [
      {
        key: "er_visits",
        label: "ER visits",
        count: 1,
        countBasis: PROGRESS_OUTCOME_COUNT_BASIS.CHECK_INS_REPORTING,
        checkInsAnswered: 4,
      },
      {
        key: "unplanned_hospital_visits",
        label: "unplanned hospital visits",
        count: 0,
        countBasis: PROGRESS_OUTCOME_COUNT_BASIS.CHECK_INS_REPORTING,
        checkInsAnswered: 4,
      },
    ],
  },
  sideEffects: {
    applicable: true,
    entries: [
      {
        id: "5077:foundational:dry_mouth",
        effect: "Dry mouth",
        effectCode: "dry_mouth",
        reportedAt: CHECK_IN_3_AT,
        product: { id: 51, name: "Daily Balance Capsules", role: "foundational" },
        actionText: "You chose to try a different foundational product.",
      },
      {
        id: "4930:bedtime:daytime_drowsiness",
        effect: "Daytime drowsiness",
        effectCode: "daytime_drowsiness",
        reportedAt: CHECK_IN_2_AT,
        product: null,
        actionText: null,
      },
    ],
  },
  thankYou: {
    applicable: true,
    checkInCount: 4,
    message: "Thank you for completing 4 check-ins. Every answer sharpens your care plan.",
    potentialDiscount: {
      amountUsd: 36,
      note: "You could have saved this on your plan with your EO member discount, whether or not you used it at checkout.",
    },
  },
};

const NEW_PATIENT_PROFILING_COMPLETED_AT = "2026-09-15T16:30:00+00:00";
const NEW_PATIENT_HORIZON_AT = "2026-10-15T16:30:00+00:00";

export const PROGRESS_NEW_PATIENT_FIXTURE: ProgressResponse = {
  schemaVersion: 2,
  isNewPatient: true,
  generatedAt: "2026-09-22T12:00:00+00:00",
  intro: {
    text: "Your charts start with your first check-in. For now this page shows where you began.",
  },
  goals: [
    {
      key: "goal_1",
      kind: PROGRESS_CHART_KIND.GOAL,
      title: "Getting through a full workday",
      subCaption: GOAL_SUB_CAPTION,
      scale: GOAL_SCALE,
      status: {
        code: PROGRESS_STATUS_CODE.JUST_STARTED,
        label: "Just Started",
        tone: PROGRESS_STATUS_TONE.NEUTRAL,
      },
      dataState: PROGRESS_DATA_STATE.NO_CHECK_INS,
      baseline: {
        value: 5,
        recordedAt: NEW_PATIENT_PROFILING_COMPLETED_AT,
        source: PROGRESS_BASELINE_SOURCE.PROFILING,
      },
      latest: null,
      points: [],
      changeFromBaseline: null,
      idealLine: {
        baseline: 5,
        severityBand: PROGRESS_SEVERITY_BAND.MODERATE,
        medianImprovement: 1.6,
        idealScore: 3.4,
        horizonAt: NEW_PATIENT_HORIZON_AT,
        patientCount: 52,
        severityMatched: true,
        caption:
          "Based on 52 patients who report the same kind of symptom as you and started at a similar level of difficulty.",
      },
      symptoms: [],
    },
    {
      key: "goal_2",
      kind: PROGRESS_CHART_KIND.GOAL,
      title: "Cooking dinner for my family",
      subCaption: GOAL_SUB_CAPTION,
      scale: GOAL_SCALE,
      status: {
        code: PROGRESS_STATUS_CODE.JUST_STARTED,
        label: "Just Started",
        tone: PROGRESS_STATUS_TONE.NEUTRAL,
      },
      dataState: PROGRESS_DATA_STATE.NO_CHECK_INS,
      baseline: {
        value: 9,
        recordedAt: NEW_PATIENT_PROFILING_COMPLETED_AT,
        source: PROGRESS_BASELINE_SOURCE.PROFILING,
      },
      latest: null,
      points: [],
      changeFromBaseline: null,
      idealLine: {
        baseline: 9,
        severityBand: PROGRESS_SEVERITY_BAND.SEVERE,
        medianImprovement: 2.4,
        idealScore: 6.6,
        horizonAt: NEW_PATIENT_HORIZON_AT,
        patientCount: 63,
        severityMatched: true,
        caption:
          "Based on 63 patients who report the same kind of symptom as you and started at a similar level of difficulty.",
      },
      symptoms: [],
    },
  ],
  symptomGroups: [
    {
      key: "symptom_group_anxiety",
      kind: PROGRESS_CHART_KIND.SYMPTOM_GROUP,
      title: "Anxiety",
      subCaption: SYMPTOM_SUB_CAPTION,
      scale: SYMPTOM_SCALE,
      status: {
        code: PROGRESS_STATUS_CODE.JUST_STARTED,
        label: "Just Started",
        tone: PROGRESS_STATUS_TONE.NEUTRAL,
      },
      dataState: PROGRESS_DATA_STATE.NO_CHECK_INS,
      baseline: {
        value: 3,
        recordedAt: NEW_PATIENT_PROFILING_COMPLETED_AT,
        source: PROGRESS_BASELINE_SOURCE.PROFILING,
      },
      latest: null,
      points: [],
      changeFromBaseline: null,
      idealLine: {
        baseline: 3,
        severityBand: PROGRESS_SEVERITY_BAND.MODERATE,
        medianImprovement: 0.5,
        idealScore: 2.5,
        horizonAt: NEW_PATIENT_HORIZON_AT,
        patientCount: 38,
        severityMatched: true,
        caption:
          "Based on 38 patients who report the same kind of symptom as you and started at a similar level of intensity.",
      },
      symptoms: [{ code: "stress_anxiety", label: "stress/anxiety" }],
    },
  ],
  qualityOfLife: {
    key: "quality_of_life",
    kind: PROGRESS_CHART_KIND.QUALITY_OF_LIFE,
    title: "Overall quality of life",
    subCaption: QUALITY_OF_LIFE_SUB_CAPTION,
    scale: QUALITY_OF_LIFE_SCALE,
    status: {
      code: PROGRESS_STATUS_CODE.JUST_STARTED,
      label: "Just Started",
      tone: PROGRESS_STATUS_TONE.NEUTRAL,
    },
    dataState: PROGRESS_DATA_STATE.NO_CHECK_INS,
    baseline: {
      value: 3,
      recordedAt: NEW_PATIENT_PROFILING_COMPLETED_AT,
      source: PROGRESS_BASELINE_SOURCE.PROFILING,
    },
    latest: null,
    points: [],
    changeFromBaseline: null,
    idealLine: {
      baseline: 3,
      severityBand: PROGRESS_SEVERITY_BAND.MODERATE,
      medianImprovement: 1,
      idealScore: 4,
      horizonAt: NEW_PATIENT_HORIZON_AT,
      patientCount: 29,
      severityMatched: true,
      caption:
        "Based on 29 patients who report the same kind of symptom as you and started at a similar quality of life.",
    },
    symptoms: [],
  },
  unscoredSymptoms: [],
  outcomes: { applicable: false, items: [] },
  sideEffects: { applicable: false, entries: [] },
  thankYou: { applicable: false, checkInCount: 0, message: null, potentialDiscount: null },
};

export const PROGRESS_RETURNING_PATIENT_RESPONSE_FIXTURE: ServiceResponse<ProgressResponse> = {
  status: 200,
  success: true,
  data: PROGRESS_RETURNING_PATIENT_FIXTURE,
};

export const PROGRESS_NEW_PATIENT_RESPONSE_FIXTURE: ServiceResponse<ProgressResponse> = {
  status: 200,
  success: true,
  data: PROGRESS_NEW_PATIENT_FIXTURE,
};
